import { Helmet } from "react-helmet-async";

import { helmetData } from "@/client/constants/helmet";

export const PrivacyPage = () => (
  <>
    <Helmet prioritizeSeoTags helmetData={helmetData}>
      <title>Privacy Policy - CareerBldr</title>
      <meta
        name="description"
        content="Privacy Policy for CareerBldr. Learn how we collect, use, and protect your personal information."
      />
    </Helmet>

    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
      <header className="mb-10">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Privacy Policy</h1>
        <p className="mt-2 text-xs text-foreground/50">Effective Date: September 3, 2026</p>
      </header>

      <div className="space-y-8">
        <section>
          <p className="text-sm leading-relaxed text-foreground/70">
            CareerBldr (&quot;the Service&quot;), operated by Studio Algorithm (&quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;), is committed to protecting your privacy. This
            Privacy Policy explains how we collect, use, store, and share your information when you
            use our AI-powered resume building platform.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            1. Information We Collect
          </h2>
          <p className="mb-3 text-sm leading-relaxed text-foreground/70">
            We collect the following types of information:
          </p>
          <ul className="space-y-2 text-sm leading-relaxed text-foreground/70">
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Account Information</strong>: Name,
                email address, and password when you create an account.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Resume Content</strong>: Work
                experience, education, skills, and other information you provide to build your
                resume.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Usage Data</strong>: Information
                about how you interact with the Service, including pages visited, features used, and
                session duration.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Device Information</strong>: Browser
                type, operating system, IP address, and device identifiers.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Payment Information</strong>:
                Billing details processed securely through our third-party payment provider. We do
                not store full credit card numbers.
              </span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            2. How We Use Your Information
          </h2>
          <ul className="space-y-2 text-sm leading-relaxed text-foreground/70">
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>To provide, maintain, and improve the Service.</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>To generate AI-powered resume suggestions and career recommendations.</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>To process subscriptions, trials, and billing through Stripe.</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>To communicate with you about updates, security alerts, and support.</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>To analyze usage patterns and improve our AI models and features.</span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>To comply with legal obligations and protect our rights.</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            3. Data Storage and Security
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We implement industry-standard security measures to protect your data, including
            encryption in transit (TLS) and at rest. Your resume data is stored on secure servers
            with restricted access. While no system is completely secure, we continuously monitor
            and improve our security practices to safeguard your information.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            4. Third-Party Services
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We may share limited information with trusted third-party services that help us operate
            the platform, including cloud hosting providers, payment processors, analytics tools,
            and AI service providers. These parties are contractually obligated to protect your data
            and may only use it for the purposes we specify. We do not sell your personal
            information to third parties. When you create an account, begin checkout or a trial, or
            subscribe after clicking an ad, we may send limited conversion and device information to
            Google (Analytics and Ads), OpenAI Ads, and TikTok Ads so they can measure whether that
            ad led to the action.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">5. Cookies</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We use cookies and similar technologies to maintain your session, remember preferences,
            and analyze usage patterns. Essential cookies are required for the Service to function.
            Analytics cookies help us understand how the Service is used. You can manage cookie
            preferences through your browser settings, though disabling essential cookies may affect
            functionality. For more information, please see our{" "}
            <a
              href="/cookies"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              Cookie Policy
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">6. Your Rights</h2>
          <p className="mb-3 text-sm leading-relaxed text-foreground/70">
            Depending on your location, you may have the following rights under applicable data
            protection laws, including the GDPR and CCPA:
          </p>
          <ul className="space-y-2 text-sm leading-relaxed text-foreground/70">
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Access</strong>: Request a copy of
                the personal data we hold about you.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Correction</strong>: Request
                correction of inaccurate or incomplete data.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Deletion</strong>: Request deletion
                of your personal data, subject to legal obligations.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Portability</strong>: Request your
                data in a structured, machine-readable format.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/30" />
              <span>
                <strong className="font-medium text-foreground">Opt-Out</strong>: Opt out of
                non-essential data processing and marketing communications.
              </span>
            </li>
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-foreground/70">
            To exercise any of these rights, contact us at{" "}
            <a
              href="mailto:privacy@careerbldr.com"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              privacy@careerbldr.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">7. Data Retention</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We retain your personal data for as long as your account is active or as needed to
            provide the Service. If you delete your account, we will remove your personal data
            within 30 days, except where retention is required by law or for legitimate business
            purposes such as fraud prevention. Anonymous, aggregated data may be retained
            indefinitely for analytics.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            8. Children&apos;s Privacy
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            The Service is not intended for children under the age of 16. We do not knowingly
            collect personal information from children. If we become aware that we have collected
            data from a child under 16, we will take steps to delete that information promptly. If
            you believe a child has provided us with personal data, please contact us.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">
            9. Changes to This Policy
          </h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            We may update this Privacy Policy from time to time to reflect changes in our practices
            or applicable laws. Material changes will be communicated via email or through a notice
            on the Service. We encourage you to review this policy periodically. Your continued use
            of the Service after changes are posted constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2 className="mb-3 mt-8 text-xl font-semibold text-foreground">10. Contact</h2>
          <p className="text-sm leading-relaxed text-foreground/70">
            If you have questions or concerns about this Privacy Policy or our data practices,
            please contact us at{" "}
            <a
              href="mailto:privacy@careerbldr.com"
              className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary/80"
            >
              privacy@careerbldr.com
            </a>
            .
          </p>
          <p className="mt-4 text-sm leading-relaxed text-foreground/70">
            CareerBldr is operated by Studio Algorithm.
          </p>
        </section>
      </div>
    </div>
  </>
);
